import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import * as XLSX from 'xlsx'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import adminStockTransferTemplateUrl from '../../assets/Admin Stock Transfer to Branch.xlsx'

const ADMIN_STOCK_TRANSFER_TEMPLATE_FILENAME = 'Admin Stock Transfer to Branch.xlsx'

const TRANSFER_MODES = [
  { value: 'warehouse_to_branch', label: 'Warehouse to Branch' },
  { value: 'branch_to_branch', label: 'Branch to Branch' }
]

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    borderColor: '#ced4da',
    '&:hover': {
      borderColor: '#ced4da'
    }
  })
}

const parseApiError = (error, fallback) => {
  if (!error.response?.data) {
    return fallback
  }
  const errorData = error.response.data
  if (errorData.detail) {
    return typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail)
  }
  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors[0]
      : errorData.non_field_errors
  }
  if (errorData.message) {
    return errorData.message
  }
  const firstError = Object.values(errorData)[0]
  return Array.isArray(firstError) ? firstError[0] : firstError || fallback
}

const emptyLineItem = () => ({
  stock_entry_id: '',
  quantity: '',
  selling_price: '',
  reorder_level: '',
  notes: ''
})

const TransferStocks = () => {
  const [transferMode, setTransferMode] = useState('warehouse_to_branch')
  const [activeTab, setActiveTab] = useState('select')

  const [formData, setFormData] = useState({
    source_warehouse_id: '',
    source_branch_id: '',
    destination_branch_id: '',
    reference_number: '',
    notes: ''
  })
  const [items, setItems] = useState([emptyLineItem()])

  const [excelFormData, setExcelFormData] = useState({
    source_warehouse_id: '',
    source_branch_id: '',
    destination_branch_id: '',
    reference_number: '',
    notes: ''
  })
  const [excelFile, setExcelFile] = useState(null)

  const [warehouses, setWarehouses] = useState([])
  const [branches, setBranches] = useState([])
  const [sourceStockEntries, setSourceStockEntries] = useState([])
  const [fetchingData, setFetchingData] = useState(true)
  const [fetchingStockEntries, setFetchingStockEntries] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [templatePreviewRows, setTemplatePreviewRows] = useState([])
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false)
  const [templatePreviewError, setTemplatePreviewError] = useState('')
  const [templatePreviewLoaded, setTemplatePreviewLoaded] = useState(false)

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        setFetchingData(true)
        const [warehousesResponse, branchesResponse] = await Promise.all([
          apiClient.get(API_ENDPOINTS.WAREHOUSES),
          apiClient.get(API_ENDPOINTS.BRANCHES)
        ])
        setWarehouses(warehousesResponse.data?.results || warehousesResponse.data || [])
        setBranches(branchesResponse.data?.results || branchesResponse.data || [])
      } catch (error) {
        console.error('Error loading transfer lookups:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: parseApiError(error, 'Failed to load warehouses and branches. Please refresh the page.')
        })
      } finally {
        setFetchingData(false)
      }
    }
    fetchLookups()
  }, [])

  useEffect(() => {
    if (activeTab !== 'excel' || templatePreviewLoaded) {
      return
    }

    const loadTemplatePreview = async () => {
      try {
        setTemplatePreviewLoading(true)
        setTemplatePreviewError('')
        const response = await fetch(adminStockTransferTemplateUrl)
        if (!response.ok) {
          throw new Error('Could not load sample template.')
        }
        const buffer = await response.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
        setTemplatePreviewRows(rows.slice(0, 6))
      } catch (error) {
        console.error('Error loading transfer template preview:', error)
        setTemplatePreviewError('Unable to load sample template preview.')
      } finally {
        setTemplatePreviewLoading(false)
        setTemplatePreviewLoaded(true)
      }
    }

    loadTemplatePreview()
  }, [activeTab, templatePreviewLoaded])

  const downloadSampleTemplate = () => {
    const link = document.createElement('a')
    link.href = adminStockTransferTemplateUrl
    link.download = ADMIN_STOCK_TRANSFER_TEMPLATE_FILENAME
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const fetchStockEntriesForWarehouse = useCallback(async (warehouseId) => {
    if (!warehouseId) {
      setSourceStockEntries([])
      return
    }
    try {
      setFetchingStockEntries(true)
      const response = await apiClient.get('/api/stock/entries/', {
        params: { warehouse_id: warehouseId }
      })
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setSourceStockEntries(data.filter((entry) => Number(entry.quantity) > 0))
    } catch (error) {
      console.error('Error fetching warehouse stock entries:', error)
      setSourceStockEntries([])
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: parseApiError(error, 'Failed to load warehouse stock entries.')
      })
    } finally {
      setFetchingStockEntries(false)
    }
  }, [])

  const fetchBranchStockForBranch = useCallback(async (branchId) => {
    if (!branchId) {
      setSourceStockEntries([])
      return
    }
    try {
      setFetchingStockEntries(true)
      const response = await apiClient.get(API_ENDPOINTS.BRANCH_STOCK, {
        params: { branch_id: branchId }
      })
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setSourceStockEntries(
        data.filter((entry) => {
          const available = entry.available_quantity_for_sale ?? entry.quantity
          return Number(available) > 0
        })
      )
    } catch (error) {
      console.error('Error fetching branch stock entries:', error)
      setSourceStockEntries([])
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: parseApiError(error, 'Failed to load branch stock entries.')
      })
    } finally {
      setFetchingStockEntries(false)
    }
  }, [])

  useEffect(() => {
    if (transferMode === 'warehouse_to_branch') {
      if (formData.source_warehouse_id) {
        fetchStockEntriesForWarehouse(formData.source_warehouse_id)
      } else {
        setSourceStockEntries([])
      }
      return
    }

    if (formData.source_branch_id) {
      fetchBranchStockForBranch(formData.source_branch_id)
    } else {
      setSourceStockEntries([])
    }
  }, [
    transferMode,
    formData.source_warehouse_id,
    formData.source_branch_id,
    fetchStockEntriesForWarehouse,
    fetchBranchStockForBranch
  ])

  const warehouseOptions = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: w.name })),
    [warehouses]
  )

  const branchOptions = useMemo(
    () =>
      branches.map((branch) => ({
        value: branch.id,
        label: `${branch.name}${branch.warehouse_name ? ` (${branch.warehouse_name})` : ''}`
      })),
    [branches]
  )

  const destinationBranchOptions = useMemo(() => {
    const sourceBranchId =
      transferMode === 'branch_to_branch' ? parseInt(formData.source_branch_id, 10) : null
    return branchOptions.filter((opt) => !sourceBranchId || opt.value !== sourceBranchId)
  }, [branchOptions, transferMode, formData.source_branch_id])

  const excelDestinationBranchOptions = useMemo(() => {
    const sourceBranchId =
      transferMode === 'branch_to_branch' ? parseInt(excelFormData.source_branch_id, 10) : null
    return branchOptions.filter((opt) => !sourceBranchId || opt.value !== sourceBranchId)
  }, [branchOptions, transferMode, excelFormData.source_branch_id])

  const sourceStockOptions = useMemo(
    () =>
      sourceStockEntries.map((entry) => {
        const availableQty =
          transferMode === 'branch_to_branch'
            ? entry.available_quantity_for_sale ?? entry.quantity
            : entry.quantity
        return {
          value: entry.id,
          label: `#${entry.id} — ${entry.product_name || 'Product'} (${entry.product_sku || 'N/A'}) • Batch ${entry.batch_number || '—'} • Available ${availableQty}`,
          entry
        }
      }),
    [sourceStockEntries, transferMode]
  )

  const selectedSourceWarehouse = useMemo(
    () => warehouseOptions.find((opt) => opt.value === parseInt(formData.source_warehouse_id, 10)) || null,
    [warehouseOptions, formData.source_warehouse_id]
  )

  const selectedSourceBranch = useMemo(
    () => branchOptions.find((opt) => opt.value === parseInt(formData.source_branch_id, 10)) || null,
    [branchOptions, formData.source_branch_id]
  )

  const selectedDestinationBranch = useMemo(
    () => destinationBranchOptions.find((opt) => opt.value === parseInt(formData.destination_branch_id, 10)) || null,
    [destinationBranchOptions, formData.destination_branch_id]
  )

  const selectedExcelSourceWarehouse = useMemo(
    () => warehouseOptions.find((opt) => opt.value === parseInt(excelFormData.source_warehouse_id, 10)) || null,
    [warehouseOptions, excelFormData.source_warehouse_id]
  )

  const selectedExcelSourceBranch = useMemo(
    () => branchOptions.find((opt) => opt.value === parseInt(excelFormData.source_branch_id, 10)) || null,
    [branchOptions, excelFormData.source_branch_id]
  )

  const selectedExcelDestinationBranch = useMemo(
    () =>
      excelDestinationBranchOptions.find(
        (opt) => opt.value === parseInt(excelFormData.destination_branch_id, 10)
      ) || null,
    [excelDestinationBranchOptions, excelFormData.destination_branch_id]
  )

  const isWarehouseMode = transferMode === 'warehouse_to_branch'
  const isBranchMode = transferMode === 'branch_to_branch'

  const hasSelectSource =
    (isWarehouseMode && formData.source_warehouse_id) || (isBranchMode && formData.source_branch_id)

  const getTransferUrls = () =>
    isBranchMode
      ? {
          submit: API_ENDPOINTS.ADMIN_MOVE_BRANCH_TO_BRANCH,
          excel: API_ENDPOINTS.ADMIN_MOVE_BRANCH_TO_BRANCH_EXCEL
        }
      : {
          submit: API_ENDPOINTS.ADMIN_MOVE_TO_BRANCH,
          excel: API_ENDPOINTS.ADMIN_MOVE_TO_BRANCH_EXCEL
        }

  const handleTransferModeChange = (mode) => {
    setTransferMode(mode)
    setFormData({
      source_warehouse_id: '',
      source_branch_id: '',
      destination_branch_id: '',
      reference_number: '',
      notes: ''
    })
    setExcelFormData({
      source_warehouse_id: '',
      source_branch_id: '',
      destination_branch_id: '',
      reference_number: '',
      notes: ''
    })
    setItems([emptyLineItem()])
    setSourceStockEntries([])
    setExcelFile(null)
    const fileInput = document.getElementById('transfer_excel_file')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleFormSelectChange = (option, field) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: option ? String(option.value) : ''
      }
      if (field === 'source_branch_id' && option && next.destination_branch_id === String(option.value)) {
        next.destination_branch_id = ''
      }
      return next
    })
    if (field === 'source_warehouse_id' || field === 'source_branch_id') {
      setItems([emptyLineItem()])
    }
  }

  const handleExcelSelectChange = (option, field) => {
    setExcelFormData((prev) => {
      const next = {
        ...prev,
        [field]: option ? String(option.value) : ''
      }
      if (field === 'source_branch_id' && option && next.destination_branch_id === String(option.value)) {
        next.destination_branch_id = ''
      }
      return next
    })
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleExcelFormChange = (e) => {
    const { name, value } = e.target
    setExcelFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const handleItemStockEntryChange = (index, option) => {
    setItems((prev) => {
      const next = [...prev]
      const entry = option?.entry
      next[index] = {
        ...next[index],
        stock_entry_id: option ? String(option.value) : '',
        selling_price: entry?.selling_price != null ? String(entry.selling_price) : next[index].selling_price,
        reorder_level: entry?.reorder_level != null ? String(entry.reorder_level) : next[index].reorder_level
      }
      return next
    })
  }

  const addItem = () => setItems((prev) => [...prev, emptyLineItem()])

  const removeItem = (index) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const getEntryById = (stockEntryId) =>
    sourceStockEntries.find((entry) => entry.id === parseInt(stockEntryId, 10))

  const getAvailableQuantity = (entry) => {
    if (!entry) return 0
    if (isBranchMode) {
      return Number(entry.available_quantity_for_sale ?? entry.quantity)
    }
    return Number(entry.quantity)
  }

  const buildItemsPayload = () => {
    const payload = []
    for (let i = 0; i < items.length; i += 1) {
      const row = items[i]
      if (!row.stock_entry_id && !row.quantity) {
        continue
      }
      if (!row.stock_entry_id || !row.quantity) {
        throw new Error(`Line ${i + 1}: select a stock entry and enter a quantity.`)
      }
      const quantity = parseFloat(row.quantity)
      if (Number.isNaN(quantity) || quantity <= 0) {
        throw new Error(`Line ${i + 1}: quantity must be greater than zero.`)
      }
      const entry = getEntryById(row.stock_entry_id)
      const availableQty = getAvailableQuantity(entry)
      if (entry && quantity > availableQty) {
        throw new Error(
          `Line ${i + 1}: quantity cannot exceed available stock (${availableQty}) for ${entry.product_name}.`
        )
      }
      const itemPayload = { quantity }
      if (isBranchMode) {
        itemPayload.branch_stock_id = parseInt(row.stock_entry_id, 10)
      } else {
        itemPayload.stock_entry_id = parseInt(row.stock_entry_id, 10)
      }
      if (row.selling_price !== '') {
        const sellingPrice = parseFloat(row.selling_price)
        if (!Number.isNaN(sellingPrice)) {
          itemPayload.selling_price = sellingPrice
        }
      }
      if (row.reorder_level !== '') {
        const reorderLevel = parseInt(row.reorder_level, 10)
        if (!Number.isNaN(reorderLevel)) {
          itemPayload.reorder_level = reorderLevel
        }
      }
      if (row.notes?.trim()) {
        itemPayload.notes = row.notes.trim()
      }
      payload.push(itemPayload)
    }
    if (!payload.length) {
      throw new Error('Add at least one stock entry line to transfer.')
    }
    return payload
  }

  const resetSelectForm = () => {
    setFormData({
      source_warehouse_id: '',
      source_branch_id: '',
      destination_branch_id: '',
      reference_number: '',
      notes: ''
    })
    setItems([emptyLineItem()])
    setSourceStockEntries([])
  }

  const buildTransferBody = (itemsPayload, sourceFormData) => {
    const body = {
      destination_branch_id: parseInt(sourceFormData.destination_branch_id, 10),
      items: itemsPayload
    }
    if (isWarehouseMode) {
      body.source_warehouse_id = parseInt(sourceFormData.source_warehouse_id, 10)
    } else {
      body.source_branch_id = parseInt(sourceFormData.source_branch_id, 10)
    }
    if (sourceFormData.reference_number.trim()) {
      body.reference_number = sourceFormData.reference_number.trim()
    }
    if (sourceFormData.notes.trim()) {
      body.notes = sourceFormData.notes.trim()
    }
    return body
  }

  const appendExcelSourceFields = (formDataToSend, sourceFormData) => {
    if (isWarehouseMode) {
      formDataToSend.append('source_warehouse_id', sourceFormData.source_warehouse_id)
    } else {
      formDataToSend.append('source_branch_id', sourceFormData.source_branch_id)
    }
    formDataToSend.append('destination_branch_id', sourceFormData.destination_branch_id)
    if (sourceFormData.reference_number.trim()) {
      formDataToSend.append('reference_number', sourceFormData.reference_number.trim())
    }
    if (sourceFormData.notes.trim()) {
      formDataToSend.append('notes', sourceFormData.notes.trim())
    }
  }

  const handleSelectSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      if (isWarehouseMode && (!formData.source_warehouse_id || !formData.destination_branch_id)) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select source warehouse and destination branch.'
        })
        return
      }
      if (isBranchMode && (!formData.source_branch_id || !formData.destination_branch_id)) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select source branch and destination branch.'
        })
        return
      }
      if (
        isBranchMode &&
        formData.source_branch_id &&
        formData.source_branch_id === formData.destination_branch_id
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Source and destination branches cannot be the same.'
        })
        return
      }

      const itemsPayload = buildItemsPayload()
      const body = buildTransferBody(itemsPayload, formData)
      const { submit } = getTransferUrls()
      const response = await apiClient.post(submit, body)
      const ref =
        response.data?.transfer?.reference_number ||
        response.data?.reference_number ||
        formData.reference_number

      Swal.fire({
        icon: 'success',
        title: 'Transfer complete',
        text: ref
          ? `Stock moved successfully (Ref: ${ref}). The transfer was completed immediately.`
          : response.data?.detail || 'Stock moved successfully.'
      })
      resetSelectForm()
    } catch (error) {
      if (error.message && !error.response) {
        Swal.fire({ icon: 'error', title: 'Validation Error', text: error.message })
        return
      }
      console.error('Error moving stock to branch:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: parseApiError(error, 'Failed to move stock to branch.')
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) {
      setExcelFile(null)
      return
    }
    if (!selectedFile.name.match(/\.xlsx$/i)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid file',
        text: 'Please upload an Excel workbook (.xlsx). Required columns: product_name, quantity.'
      })
      e.target.value = ''
      setExcelFile(null)
      return
    }
    setExcelFile(selectedFile)
  }

  const formatExcelResultMessage = (data) => {
    const summary = data?.summary
    const errors = data?.errors || []
    const created = data?.created || []
    let message = summary
      ? `${summary.successful ?? 0} row(s) succeeded, ${summary.failed ?? 0} failed.`
      : data?.detail || 'Import processed.'
    if (created[0]?.reference_number) {
      message = `Transfer ${created[0].reference_number} created. ${message}`
    }
    if (errors.length) {
      const preview = errors
        .slice(0, 5)
        .map((err) => `Row ${err.row}: ${err.error}`)
        .join('<br/>')
      message += `<br/><br/><strong>Errors:</strong><br/>${preview}`
      if (errors.length > 5) {
        message += `<br/>…and ${errors.length - 5} more.`
      }
    }
    return message
  }

  const handleExcelSubmit = async (e) => {
    e.preventDefault()
    setExcelLoading(true)
    try {
      if (isWarehouseMode && (!excelFormData.source_warehouse_id || !excelFormData.destination_branch_id)) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select source warehouse and destination branch.'
        })
        return
      }
      if (isBranchMode && (!excelFormData.source_branch_id || !excelFormData.destination_branch_id)) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select source branch and destination branch.'
        })
        return
      }
      if (
        isBranchMode &&
        excelFormData.source_branch_id &&
        excelFormData.source_branch_id === excelFormData.destination_branch_id
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Source and destination branches cannot be the same.'
        })
        return
      }
      if (!excelFile) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select an Excel file to upload.'
        })
        return
      }

      const formDataToSend = new FormData()
      formDataToSend.append('file', excelFile)
      appendExcelSourceFields(formDataToSend, excelFormData)

      const { excel } = getTransferUrls()
      const response = await apiClient.post(excel, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      const hasErrors = (response.data?.errors?.length ?? 0) > 0
      const failed = response.data?.summary?.failed ?? 0

      Swal.fire({
        icon: hasErrors && failed > 0 ? 'warning' : 'success',
        title: hasErrors && failed > 0 ? 'Completed with issues' : 'Import successful',
        html: formatExcelResultMessage(response.data)
      })

      setExcelFormData({
        source_warehouse_id: '',
        source_branch_id: '',
        destination_branch_id: '',
        reference_number: '',
        notes: ''
      })
      setExcelFile(null)
      const fileInput = document.getElementById('transfer_excel_file')
      if (fileInput) {
        fileInput.value = ''
      }
    } catch (error) {
      console.error('Error importing transfer Excel:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: parseApiError(error, 'Failed to import Excel transfer.')
      })
    } finally {
      setExcelLoading(false)
    }
  }

  const formDisabled = submitting || fetchingData
  const excelDisabled = excelLoading || fetchingData

  return (
    <>
      <div className="pagetitle">
        <h1>Transfer Stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Stocks</li>
            <li className="breadcrumb-item active">Transfer Stocks</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-arrow-left-right me-2" />
                  Transfer Stock to Branch
                </h5>
                <p className="text-muted">
                  Move stock from a warehouse or another branch to a destination branch immediately. Admin transfers complete without approval.
                </p>

                <div className="row mb-4">
                  <label htmlFor="transfer_mode" className="col-sm-2 col-form-label">
                    Transfer type <span className="text-danger">*</span>
                  </label>
                  <div className="col-sm-10">
                    <Select
                      inputId="transfer_mode"
                      value={TRANSFER_MODES.find((mode) => mode.value === transferMode) || null}
                      onChange={(option) => handleTransferModeChange(option?.value || 'warehouse_to_branch')}
                      options={TRANSFER_MODES}
                      isSearchable={false}
                      isDisabled={formDisabled || excelDisabled}
                      styles={selectStyles}
                    />
                  </div>
                </div>

                <ul className="nav nav-tabs mb-3" id="transferStocksTabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'select' ? 'active' : ''}`}
                      id="select-tab"
                      onClick={() => setActiveTab('select')}
                    >
                      <i className="bi bi-ui-checks-grid me-2" />
                      Select Stock Entries
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'excel' ? 'active' : ''}`}
                      id="excel-tab"
                      onClick={() => setActiveTab('excel')}
                    >
                      <i className="bi bi-file-earmark-excel me-2" />
                      Import from Excel
                    </button>
                  </li>
                </ul>

                <div className="tab-content">
                  <div
                    className={`tab-pane fade ${activeTab === 'select' ? 'show active' : ''}`}
                    id="select-panel"
                    role="tabpanel"
                  >
                    <form onSubmit={handleSelectSubmit}>
                      {isWarehouseMode ? (
                        <div className="row mb-3">
                          <label htmlFor="source_warehouse_id" className="col-sm-2 col-form-label">
                            Source warehouse <span className="text-danger">*</span>
                          </label>
                          <div className="col-sm-10">
                            <Select
                              inputId="source_warehouse_id"
                              value={selectedSourceWarehouse}
                              onChange={(option) => handleFormSelectChange(option, 'source_warehouse_id')}
                              options={warehouseOptions}
                              isClearable
                              isSearchable
                              placeholder="Select source warehouse"
                              isDisabled={formDisabled}
                              styles={selectStyles}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="row mb-3">
                          <label htmlFor="source_branch_id" className="col-sm-2 col-form-label">
                            Source branch <span className="text-danger">*</span>
                          </label>
                          <div className="col-sm-10">
                            <Select
                              inputId="source_branch_id"
                              value={selectedSourceBranch}
                              onChange={(option) => handleFormSelectChange(option, 'source_branch_id')}
                              options={branchOptions}
                              isClearable
                              isSearchable
                              placeholder="Select source branch"
                              isDisabled={formDisabled}
                              styles={selectStyles}
                            />
                          </div>
                        </div>
                      )}

                      <div className="row mb-3">
                        <label htmlFor="destination_branch_id" className="col-sm-2 col-form-label">
                          Destination branch <span className="text-danger">*</span>
                        </label>
                        <div className="col-sm-10">
                          <Select
                            inputId="destination_branch_id"
                            value={selectedDestinationBranch}
                            onChange={(option) => handleFormSelectChange(option, 'destination_branch_id')}
                            options={destinationBranchOptions}
                            isClearable
                            isSearchable
                            placeholder="Select destination branch"
                            isDisabled={formDisabled}
                            styles={selectStyles}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        {/* <label htmlFor="reference_number" className="col-sm-2 col-form-label">
                          Reference number
                        </label> */}
                        {/* <div className="col-sm-10">
                          <input
                            type="text"
                            className="form-control"
                            id="reference_number"
                            name="reference_number"
                            value={formData.reference_number}
                            onChange={handleFormChange}
                            placeholder="Auto-generated if left blank"
                            disabled={formDisabled}
                          />
                        </div> */}
                      </div>

                      {hasSelectSource && (
                        <div className="alert alert-light border mb-3 py-2">
                          {fetchingStockEntries ? (
                            <span className="text-muted">
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              Loading {isWarehouseMode ? 'warehouse' : 'branch'} stock entries…
                            </span>
                          ) : (
                            <span className="text-muted">
                              {sourceStockEntries.length
                                ? `${sourceStockEntries.length} stock entr${sourceStockEntries.length === 1 ? 'y' : 'ies'} with available quantity.`
                                : `No stock entries with available quantity in this ${isWarehouseMode ? 'warehouse' : 'branch'}.`}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="row mb-3">
                        <label className="col-sm-2 col-form-label">Stock lines</label>
                        <div className="col-sm-10">
                          {items.map((item, index) => (
                            <div key={index} className="card mb-3">
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="card-title mb-0">Line {index + 1}</h6>
                                  {items.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeItem(index)}
                                      disabled={formDisabled}
                                    >
                                      <i className="bi bi-trash" /> Remove
                                    </button>
                                  )}
                                </div>

                                <div className="row mb-2">
                                  <label className="col-sm-3 col-form-label">
                                    Stock entry <span className="text-danger">*</span>
                                  </label>
                                  <div className="col-sm-9">
                                    <Select
                                      value={
                                        item.stock_entry_id
                                          ? sourceStockOptions.find(
                                              (opt) => opt.value === parseInt(item.stock_entry_id, 10)
                                            ) || null
                                          : null
                                      }
                                      onChange={(option) => handleItemStockEntryChange(index, option)}
                                      options={sourceStockOptions}
                                      isClearable
                                      isSearchable
                                      placeholder={
                                        hasSelectSource
                                          ? `Select ${isWarehouseMode ? 'warehouse' : 'branch'} stock entry`
                                          : `Select a source ${isWarehouseMode ? 'warehouse' : 'branch'} first`
                                      }
                                      isDisabled={formDisabled || !hasSelectSource || fetchingStockEntries}
                                      styles={selectStyles}
                                    />
                                  </div>
                                </div>

                                <div className="row mb-2">
                                  <label className="col-sm-3 col-form-label">
                                    Quantity <span className="text-danger">*</span>
                                  </label>
                                  <div className="col-sm-9">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="form-control"
                                      value={item.quantity}
                                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                      placeholder="Units to move"
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>

                                <div className="row mb-2">
                                  <label className="col-sm-3 col-form-label">Selling price</label>
                                  <div className="col-sm-9">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="form-control"
                                      value={item.selling_price}
                                      onChange={(e) => handleItemChange(index, 'selling_price', e.target.value)}
                                      placeholder="Optional — uses source entry price if blank"
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>

                                <div className="row mb-2">
                                  <label className="col-sm-3 col-form-label">Reorder level</label>
                                  <div className="col-sm-9">
                                    <input
                                      type="number"
                                      step="1"
                                      min="0"
                                      className="form-control"
                                      value={item.reorder_level}
                                      onChange={(e) => handleItemChange(index, 'reorder_level', e.target.value)}
                                      placeholder="Optional at destination branch"
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>

                                <div className="row">
                                  <label className="col-sm-3 col-form-label">Line notes</label>
                                  <div className="col-sm-9">
                                    <textarea
                                      className="form-control"
                                      rows="2"
                                      value={item.notes}
                                      onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                      placeholder="Optional"
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={addItem}
                            disabled={formDisabled || !hasSelectSource}
                          >
                            <i className="bi bi-plus-circle me-2" />
                            Add another line
                          </button>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="notes" className="col-sm-2 col-form-label">Transfer notes</label>
                        <div className="col-sm-10">
                          <textarea
                            className="form-control"
                            id="notes"
                            name="notes"
                            rows="3"
                            value={formData.notes}
                            onChange={handleFormChange}
                            placeholder="Optional"
                            disabled={formDisabled}
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-10 offset-sm-2">
                          <button type="submit" className="btn btn-primary" disabled={formDisabled}>
                            {submitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Moving stock…
                              </>
                            ) : (
                              <>
                                <i className="bi bi-arrow-left-right me-2" />
                                Move to branch
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>

                  <div
                    className={`tab-pane fade ${activeTab === 'excel' ? 'show active' : ''}`}
                    id="excel-panel"
                    role="tabpanel"
                  >
                    <div
                      className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-3 border-bottom"
                    >
                      <div>
                        <h6 className="mb-1 fw-semibold">Sample Excel format</h6>
                        <small className="text-muted">
                          Download the template, fill in your products, then upload it below.
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={downloadSampleTemplate}
                      >
                        <i className="bi bi-download me-1" />
                        Download sample file
                      </button>
                    </div>

                   

                    {/* <div className="mb-4">
                      <h6 className="fw-bold mb-2">
                        <i className="bi bi-table me-2" />
                        Sample file preview
                      </h6>
                      {templatePreviewLoading && (
                        <p className="text-muted small mb-0">
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Loading sample…
                        </p>
                      )}
                      {!templatePreviewLoading && templatePreviewError && (
                        <p className="text-danger small mb-0">{templatePreviewError}</p>
                      )}
                      {!templatePreviewLoading && !templatePreviewError && templatePreviewRows.length > 0 && (
                        <div className="table-responsive border rounded">
                          <table className="table table-bordered table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                {templatePreviewRows[0].map((header, index) => (
                                  <th key={index}>{header}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {templatePreviewRows.slice(1).map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {templatePreviewRows[0].map((_, colIndex) => (
                                    <td key={colIndex}>{row[colIndex] ?? ''}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div> */}

                    <form onSubmit={handleExcelSubmit}>
                      {isWarehouseMode ? (
                        <div className="row mb-3">
                          <label htmlFor="excel_source_warehouse_id" className="col-sm-2 col-form-label">
                            Source warehouse <span className="text-danger">*</span>
                          </label>
                          <div className="col-sm-10">
                            <Select
                              inputId="excel_source_warehouse_id"
                              value={selectedExcelSourceWarehouse}
                              onChange={(option) => handleExcelSelectChange(option, 'source_warehouse_id')}
                              options={warehouseOptions}
                              isClearable
                              isSearchable
                              placeholder="Select source warehouse"
                              isDisabled={excelDisabled}
                              styles={selectStyles}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="row mb-3">
                          <label htmlFor="excel_source_branch_id" className="col-sm-2 col-form-label">
                            Source branch <span className="text-danger">*</span>
                          </label>
                          <div className="col-sm-10">
                            <Select
                              inputId="excel_source_branch_id"
                              value={selectedExcelSourceBranch}
                              onChange={(option) => handleExcelSelectChange(option, 'source_branch_id')}
                              options={branchOptions}
                              isClearable
                              isSearchable
                              placeholder="Select source branch"
                              isDisabled={excelDisabled}
                              styles={selectStyles}
                            />
                          </div>
                        </div>
                      )}

                      <div className="row mb-3">
                        <label htmlFor="excel_destination_branch_id" className="col-sm-2 col-form-label">
                          Destination branch <span className="text-danger">*</span>
                        </label>
                        <div className="col-sm-10">
                          <Select
                            inputId="excel_destination_branch_id"
                            value={selectedExcelDestinationBranch}
                            onChange={(option) => handleExcelSelectChange(option, 'destination_branch_id')}
                            options={excelDestinationBranchOptions}
                            isClearable
                            isSearchable
                            placeholder="Select destination branch"
                            isDisabled={excelDisabled}
                            styles={selectStyles}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="excel_reference_number" className="col-sm-2 col-form-label">
                          Reference number
                        </label>
                        <div className="col-sm-10">
                          <input
                            type="text"
                            className="form-control"
                            id="excel_reference_number"
                            name="reference_number"
                            value={excelFormData.reference_number}
                            onChange={handleExcelFormChange}
                            placeholder="Auto-generated if left blank"
                            disabled={excelDisabled}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="excel_notes" className="col-sm-2 col-form-label">Transfer notes</label>
                        <div className="col-sm-10">
                          <textarea
                            className="form-control"
                            id="excel_notes"
                            name="notes"
                            rows="2"
                            value={excelFormData.notes}
                            onChange={handleExcelFormChange}
                            placeholder="Optional"
                            disabled={excelDisabled}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="transfer_excel_file" className="col-sm-2 col-form-label">
                          Excel file <span className="text-danger">*</span>
                        </label>
                        <div className="col-sm-10">
                          <input
                            type="file"
                            className="form-control"
                            id="transfer_excel_file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleFileChange}
                            disabled={excelDisabled}
                          />
                          <small className="form-text text-muted d-block mt-1">
                            Use the sample file above (.xlsx). Column headers must match the template (case-insensitive).
                          </small>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-10 offset-sm-2">
                          <button type="submit" className="btn btn-primary" disabled={excelDisabled}>
                            {excelLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Uploading…
                              </>
                            ) : (
                              <>
                                <i className="bi bi-upload me-2" />
                                Import and move to branch
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default TransferStocks
